import { Box, Flex, Group, Pill, ScrollArea, Stack, Text, Title } from '@mantine/core';
import { useMemo } from 'react';
import type { FC, ReactNode } from 'react';
import type { DeepPartial } from 'react-hook-form';

import { useVenueItemQuery } from '../../../data/venues/useVenueItemQuery.ts';
import { env } from '../../../env.ts';
import type { IncompleteEventFormComplete } from '../../../forms/event/IncompleteEventForm/schema.ts';
import { capitalizeFirstLetter } from '../../../helpers/stringHelpers.ts';
import { useEventOptions } from '../../../hooks/useEventOptions/useEventOptions.ts';
import { useI18n } from '../../../hooks/useI18n.ts';
import { getBlockText } from '../../../lib/getBlockText.ts';
import { notEmpty } from '../../../lib/notEmpty.ts';
import { EventDates } from './EventDates.tsx';
import { EventImage } from './EventImage.tsx';

export const EventPreview: FC<
  DeepPartial<IncompleteEventFormComplete & { isHorizontal: boolean }> & { afterImageContent?: ReactNode }
> = ({
  afterImageContent,
  name,
  image,
  description,
  tags,
  category,
  start,
  end,
  venue,
  eventType,
  customVenue,
  eventDetails,
  isHorizontal = false,
  recurrenceActive,
  recurrenceRate,
  recurrenceAmount,
  recurrenceMonthType,
}) => {
  const { category: allCategories, targetAudiences: allTargetAudiences, detail: allEventDetails } = useEventOptions();
  const { data: venueData } = useVenueItemQuery(venue ?? undefined);

  const { t } = useI18n();

  const descriptionBlocks = useMemo(() => {
    if (
      !description ||
      !('blocks' in description) ||
      (description.blocks?.length ?? 0) < 1 ||
      getBlockText(description.blocks?.[0]) === ''
    ) {
      return;
    }

    return description?.blocks
      ?.filter((block) => block?.type === 'paragraph')
      .map((block) => (
        <Box
          // oxlint-disable-next-line react/no-danger -- paragraph blocks are Editor.js HTML (bold, italic, links) and must render as such
          dangerouslySetInnerHTML={{ __html: getBlockText(block) ?? '' }}
          key={block?.id}
          style={{ wordBreak: 'break-word', fontSize: 20 }}
        />
      ));
  }, [description]);

  const location = useMemo(() => {
    if (eventType === 'online') {
      return (
        <Pill bg="#f3f4f6" c="#000" w="fit-content">
          {t('event-preview_option--location-online') ?? 'Online'}
        </Pill>
      );
    }

    if (eventType === 'secret') {
      return (
        <Pill bg="#f3f4f6" c="#000" w="fit-content">
          {t('event-preview_option--location-secret') ?? 'Secret'}
        </Pill>
      );
    }

    if (eventType === 'live' && venueData) {
      return <Text size="sm">{venueData.commercial_name}</Text>;
    }

    if (eventType === 'live' && customVenue) {
      return <Text size="sm">{customVenue} (custom)</Text>;
    }

    return <Text size="sm">{t('event-preview_message--no-location') ?? 'No location selected yet'}</Text>;
  }, [customVenue, eventType, venueData, t]);

  const categoryLabel = allCategories?.data?.find((item) => item.value === category)?.label;

  const activeTags = allTargetAudiences.data
    ?.filter((item) => tags?.filter(notEmpty).includes(item.value))
    .toSorted((a, b) => a.label.localeCompare(b.label));

  const activeEventDetails = allEventDetails.data
    ?.filter((item) => eventDetails?.filter(notEmpty).includes(item.value))
    .toSorted((a, b) => a.label.localeCompare(b.label));

  return (
    <Flex
      align={isHorizontal ? 'flex-start' : 'center'}
      direction={isHorizontal ? 'row' : 'column'}
      gap="sm"
      w={isHorizontal ? 'auto' : 320}
    >
      <EventImage src={image ? `${env.VITE_DIRECTUS_API_URL}/assets/${image}` : undefined} />
      {afterImageContent && (
        <Box px="xs" w="100%">
          {afterImageContent}
        </Box>
      )}
      <Stack gap="xs" pb="sm" px="xs" style={{ alignSelf: isHorizontal ? undefined : 'flex-start' }}>
        <Stack gap="xs">
          <Title c={name ? 'black' : 'gray.5'} order={1} size="sm" style={{ wordBreak: 'break-word' }}>
            {name && name !== '' ? name : (t('event-preview_message--no-title') ?? 'No title set yet')}
          </Title>
          {descriptionBlocks ? (
            <ScrollArea.Autosize mah={163} mr="auto">
              <Stack gap="xs" justify="flex-start">
                {descriptionBlocks}
              </Stack>
            </ScrollArea.Autosize>
          ) : (
            <Text size="sm">{t('event-preview_message--no-description') ?? 'No description present yet'}</Text>
          )}
        </Stack>
        <Stack gap={2}>
          <Text c="gigi-grey-text" fw="600" size="xxs">
            {t('event-preview_label--category') ?? 'Category:'}
          </Text>
          <Box>
            {categoryLabel ? (
              <Pill bg="#f3f4f6" c="#000">
                {capitalizeFirstLetter(categoryLabel)}
              </Pill>
            ) : (
              <Text size="sm">{t('event-preview_message--no-category') ?? 'No category selected'}</Text>
            )}
          </Box>
        </Stack>
        <Stack gap={2}>
          <Text c="gigi-grey-text" fw="600" size="xxs">
            {t('event-preview_label--location') ?? 'Location:'}
          </Text>
          {location}
        </Stack>
        <EventDates
          end={end}
          recurrenceActive={recurrenceActive}
          recurrenceAmount={recurrenceAmount}
          recurrenceMonthType={recurrenceMonthType}
          recurrenceRate={recurrenceRate}
          start={start}
        />
        <Stack gap={2}>
          <Text c="gigi-grey-text" fw="600" size="xxs">
            {t('event-preview_label--target-audience') ?? 'Who is it for:'}
          </Text>
          {activeTags.length > 0 ? (
            <Group gap="xxxs">
              {activeTags.map((item) => (
                <Pill bg="#f3f4f6" c="#000" key={item.value}>
                  {capitalizeFirstLetter(item.label)}
                </Pill>
              ))}
            </Group>
          ) : (
            <Text size="sm">{t('event-preview_message--no-tags') ?? 'No tags selected yet'}</Text>
          )}
        </Stack>
        <Stack gap={2}>
          <Text c="gigi-grey-text" fw="600" size="xxs">
            {t('event-preview_label--event-details') ?? 'Event Details:'}
          </Text>
          {activeEventDetails.length > 0 ? (
            <Group gap="xs">
              {activeEventDetails.map((item) => (
                <Pill bg="#f3f4f6" c="#000" key={item.value}>
                  {item.label}
                </Pill>
              ))}
            </Group>
          ) : (
            <Text size="sm">{t('event-preview_message--no-details') ?? 'No event details selected yet'}</Text>
          )}
        </Stack>
      </Stack>
    </Flex>
  );
};
